/* eslint-disable @typescript-eslint/prefer-optional-chain */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TableRow,
  TableHead,
  Table,
  TableBody,
  Typography,
  TableCell,
} from '@mui/material';
import { type Asset } from 'types/asset.types';
import { PNG } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import { RunButton, pulse } from './Components/RunButton';
import TestStatus from './Components/TestStatus';
import {
  ButtonWrapper,
  LeftSection,
  RightSection,
  ContentContainer,
} from './Components/StyledComponents';
import TagHandler from 'handlers/tag.handler';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AssetProfileChart from './AssetProfileChart';
import { ThemePalette } from 'mui.theme';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _dark = clientThemePalette[CLIENT_THEME].dark;

const MainBody = ({
  enableDarkTheme,
  makeModel,
  asset,
}: {
  enableDarkTheme: boolean;
  makeModel: string;
  asset: Asset;
}) => {
  const { t } = useTranslation();
  const [isRunning, setIsRunning] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // const [selectedBox, setSelectedBox] = useState('brakes');

  // States for processing incoming data
  const [brakeData, setBrakeData] = useState<any>(null);
  const [chassisData, setChassisData] = useState<any>(null);
  const [engineData, setEngineData] = useState<any>(null);
  const [processingStatus, setProcessingStatus] = useState({
    brakes: 'idle', // 'idle' | 'processing' | 'completed' | 'error'
    chassis: 'idle',
    engine: 'idle',
  });

  const [brakeBreachCount, setBrakeBreachCount] = useState(0);
  const [chassisBreachCount, setChassisBreachCount] = useState(0);
  const [engineBreachCount, setEngineBreachCount] = useState(0);

  // State for managing which analysis section is displayed
  const [selectedAnalysisSection, setSelectedAnalysisSection] =
    useState('brakes');

  const [selectedSensors, setSelectedSensors] = useState<
    Array<{ sensor: string; redsPercentage: string; ts: string }>
  >([]);

  const handlePointClick = (
    sensors: Array<{ sensor: string; redsPercentage: string; ts: string }>
  ) => {
    setSelectedSensors(sensors);
  };

  const handleClearSensors = () => {
    setSelectedSensors([]);
  };

  const tagHandler = new TagHandler();

  const pngName = getImageName(asset, 'Large');

  const { timezoneBasedOnDisplayMode } = useTimeDisplayMode();

  dayjs.extend(utc);

  const endDate = dayjs().utc().toDate();
  const startDate = dayjs().utc().subtract(24, 'hours').toDate();

  const processIncomingData = (
    dataType: 'brakes' | 'chassis' | 'engine',
    data: any
  ) => {
    switch (dataType) {
      case 'brakes':
        setBrakeData(data);
        setProcessingStatus((prev) => ({ ...prev, brakes: 'completed' }));
        break;
      case 'chassis':
        setChassisData(data);
        setProcessingStatus((prev) => ({ ...prev, chassis: 'completed' }));
        break;
      case 'engine':
        setEngineData(data);
        setProcessingStatus((prev) => ({ ...prev, engine: 'completed' }));
        break;
    }

    // Check if all data has been processed
    // const allCompleted = Object.values(processingStatus).every(
    //   (status) => status === 'completed'
    // );
    // if (allCompleted) {
    //   console.log('All virtual load test data has been processed!');
    //   // You can trigger additional actions here when all data is ready
    // }
  };

  const getTagDetails = async () => {
    // Set all statuses to processing
    setProcessingStatus({
      brakes: 'processing',
      chassis: 'processing',
      engine: 'processing',
    });

    try {
      await tagHandler.getTagsForVirtualLoadTest(
        asset.device.deviceId,
        startDate,
        endDate,
        (dataType: 'brakes' | 'chassis' | 'engine', data: any) => {
          processIncomingData(dataType, data);
        }
      );
    } catch (error) {
      setProcessingStatus({
        brakes: 'error',
        chassis: 'error',
        engine: 'error',
      });
    }
  };

  useEffect(() => {
    if (isExpanded) {
      void getTagDetails();
    }
  }, [isExpanded]);

  useEffect(() => {
    const allCompleted = Object.values(processingStatus).every(
      (status) => status === 'completed' || status === 'error'
    );

    if (allCompleted && isRunning) {
      setIsRunning(false);
    }
  }, [processingStatus, isRunning]);

  const handleRunTest = () => {
    setIsRunning(true);
    setIsExpanded(true);
  };

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? _dark.boxBackground
          : _dark.boxBackground,
        position: 'relative',
        width: '100%',
        height: isExpanded ? '80vh' : '100vh',
        overflow: 'hidden',
        marginTop: '20px',
        borderRadius: '5px',
      }}
    >
      <LeftSection isExpanded={isExpanded} enableDarkTheme={enableDarkTheme}>
        <ContentContainer isExpanded={isExpanded}>
          <PNG
            name={pngName}
            style={{
              width: isExpanded ? '100%' : '70%',
              position: 'absolute',
              top: isExpanded ? '5%' : '30%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />

          {isExpanded && (
            <TestStatus
              enableDarkTheme={enableDarkTheme}
              processingStatus={processingStatus}
              brakeBreachCount={brakeBreachCount}
              chassisBreachCount={chassisBreachCount}
              engineBreachCount={engineBreachCount}
              onAnalysisClick={(section) => {
                setSelectedAnalysisSection(section);
              }}
              selectedAnalysisSection={selectedAnalysisSection}
            />
          )}

          <ButtonWrapper isExpanded={isExpanded}>
            <RunButton
              onClick={handleRunTest}
              disabled={isRunning}
              isExpanded={isExpanded}
              sx={{
                ...(isRunning && {
                  background:
                    'linear-gradient(135deg, #4caf50 0%, #66bb6a 50%, #4caf50 100%)',
                  animation: `${pulse} 1s infinite`,
                  '&:hover': {
                    background:
                      'linear-gradient(135deg, #4caf50 0%, #66bb6a 50%, #4caf50 100%)',
                    transform: 'translateY(-5px) scale(1.05)',
                  },
                }),
              }}
            >
              {isRunning || (isExpanded && isRunning)
                ? 'Running Test...'
                : isExpanded && !isRunning
                ? 'Re-Run Virtual Load Test'
                : 'Run Virtual Load Test'}
            </RunButton>
          </ButtonWrapper>
        </ContentContainer>
      </LeftSection>

      <RightSection isExpanded={isExpanded}>
        {/* Brakes Analysis Section */}
        {selectedAnalysisSection === 'brakes' && (
          <Box sx={{ height: '100%', overflowY: 'scroll' }}>
            <Box
              sx={{
                // width: '100%',
                height: 'auto',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: enableDarkTheme
                  ? alpha(_dark.primary, 0.15)
                  : 'rgba(136, 136, 136, 0.4)',
                borderRadius: '12px',
                border: enableDarkTheme
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
                margin: '3% 3% 0 3%',
              }}
            >
              <Box
                sx={{
                  backgroundColor: enableDarkTheme
                    ? _dark.boxBackground
                    : _dark.boxBackground,
                  borderRadius: '12px',
                  border: enableDarkTheme
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  padding: '24px 24px 12px 24px',
                }}
              >
                {/* Header */}
                <Box sx={{ marginBottom: '10px' }}>
                  <Typography
                    sx={{
                      color: enableDarkTheme ? 'white' : 'black',
                      fontSize: '20px',
                      fontWeight: 600,
                      textAlign: 'left',
                    }}
                  >
                    {t('brakes_struts_analysis')}
                  </Typography>
                  <Typography
                    sx={{
                      color: enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.7)'
                        : 'rgba(0, 0, 0, 0.7)',
                      fontSize: '14px',
                      marginTop: '4px',
                    }}
                  >
                    {t(
                      'performance_metrics_and_trend_analysis_for_brake_system_and_'
                    )}
                  </Typography>
                </Box>
                {/* Content Grid */}
                <Grid container spacing={3} sx={{ minHeight: '350px' }}>
                  {/* Chart Section - 70% */}
                  <Grid item xs={8.4}>
                    {brakeData ? (
                      <AssetProfileChart
                        groupedMovingAverage={brakeData}
                        asset={asset.device.deviceId}
                        setSensorsInRedCategory={() => {}}
                        enableDarkTheme={enableDarkTheme}
                        onPointClick={handlePointClick}
                        onClearSensors={handleClearSensors}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 300,
                          backgroundColor: enableDarkTheme
                            ? _dark.boxBackground
                            : _dark.boxBackground,
                          borderRadius: '8px',
                          border: enableDarkTheme
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Typography
                          sx={{
                            color: enableDarkTheme
                              ? 'rgba(255, 255, 255, 0.5)'
                              : 'rgba(0, 0, 0, 0.5)',
                            fontSize: '16px',
                          }}
                        >
                          {t('loading_chart_data')}
                        </Typography>
                      </Box>
                    )}
                  </Grid>

                  {/* Analysis Section - 30% */}
                  <Grid item xs={3.6}>
                    <Box
                      sx={{
                        height: '300px',
                        backgroundColor: enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.02)'
                          : 'rgba(0, 0, 0, 0.02)',
                        borderRadius: '8px',
                        border: enableDarkTheme
                          ? '1px solid rgba(255, 255, 255, 0.05)'
                          : '1px solid rgba(0, 0, 0, 0.05)',
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography
                        sx={{
                          color: enableDarkTheme ? 'white' : 'black',
                          fontSize: '18px',
                          fontWeight: 600,
                          marginBottom: '10px',
                        }}
                      >
                        {t('key_metrics')}
                      </Typography>

                      {/* Metrics Content */}
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {selectedSensors.length > 0 ? (
                          <Box
                            sx={{
                              padding: '12px',
                              backgroundColor: enableDarkTheme
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.05)',
                              borderRadius: '6px',
                            }}
                          >
                            <Typography
                              sx={{
                                color: enableDarkTheme
                                  ? 'rgba(255, 255, 255, 0.8)'
                                  : 'rgba(0, 0, 0, 0.8)',
                                fontSize: '13px',
                                fontWeight: 'bold',
                              }}
                            >
                              {t('sensors_in')}{' '}
                              <span style={{ color: '#F44336' }}>Red</span> Zone
                            </Typography>
                            <Typography
                              sx={{
                                color: enableDarkTheme
                                  ? 'rgba(255, 255, 255, 0.8)'
                                  : 'rgba(0, 0, 0, 0.8)',
                                fontSize: '12px',
                                marginBottom: '3px',
                              }}
                            >
                              {dayjs(parseInt(selectedSensors[0].ts)).format(
                                'YYYY-MM-DD h:mm A'
                              )}
                            </Typography>
                            <ul
                              style={{
                                listStyle: 'number',
                                padding: '5px 20px',
                                width: '100%',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                lineHeight: '3',
                                color: enableDarkTheme
                                  ? 'rgba(200, 197, 197, 1)'
                                  : 'rgba(0, 0, 0, 0.8)',
                              }}
                            >
                              {selectedSensors.map((sensor, index) => (
                                <li key={index}>
                                  {sensor.sensor}:{' '}
                                  <span
                                    style={{
                                      fontSize: '12px',
                                      color: 'rgb(216, 106, 106)',
                                    }}
                                  >
                                    {sensor.redsPercentage}%
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              color: enableDarkTheme
                                ? 'rgba(255, 255, 255, 0.5)'
                                : 'rgba(0, 0, 0, 0.5)',
                              fontSize: '14px',
                              textAlign: 'center',
                            }}
                          >
                            {t(
                              'click_a_point_on_the_chart_to_see_sensor_details'
                            )}
                          </Typography>
                        )}
                      </Box>

                      {/* Sensors List Box */}
                      {/* {brakeData && (
                      <Box
                        sx={{
                          backgroundColor: enableDarkTheme
                            ? 'rgba(255, 255, 255, 0.05)'
                            : 'rgba(0, 0, 0, 0.02)',
                          borderRadius: '8px',
                          padding: '8px',
                          marginTop: '5px',
                          border: enableDarkTheme
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: enableDarkTheme
                              ? 'rgba(255, 255, 255, 0.9)'
                              : 'rgba(0, 0, 0, 0.9)',
                            marginBottom: '5px',
                          }}
                        >
                          Sensors considered for Brakes & Struts
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                          }}
                        >
                          {Array.from(
                            new Set(
                              brakeData.map(
                                (item: any) =>
                                  item.device_sensor?.S?.split('-')[1]?.replace(
                                    /_/g,
                                    ' '
                                  ) || 'Unknown'
                              )
                            )
                          ).map((sensor, index) => (
                            <Box
                              key={index}
                              sx={{
                                backgroundColor: enableDarkTheme
                                  ? 'rgba(33, 150, 243, 0.2)'
                                  : 'rgba(33, 150, 243, 0.1)',
                                color: enableDarkTheme
                                  ? 'rgba(33, 150, 243, 0.9)'
                                  : 'rgba(33, 150, 243, 0.8)',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: 500,
                                border: enableDarkTheme
                                  ? '1px solid rgba(33, 150, 243, 0.3)'
                                  : '1px solid rgba(33, 150, 243, 0.2)',
                              }}
                            >
                              {sensor as string}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )} */}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box
              sx={{
                // width: '100%',
                height: 'auto',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                backgroundColor: enableDarkTheme
                  ? 'rgba(141, 142, 144, 0.4)'
                  : 'rgba(136, 136, 136, 0.4)',
                borderRadius: '12px',
                border: enableDarkTheme
                  ? '1px solid rgba(117, 114, 114, 0.1)'
                  : '1px solid rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                boxShadow: enableDarkTheme
                  ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                  : '0 4px 20px rgba(0, 0, 0, 0.1)',
                margin: '1% 3% 3% 3%',
              }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  padding: '20px 24px 16px 24px',
                  borderBottom: enableDarkTheme
                    ? '1px solid rgba(255, 255, 255, 0.1)'
                    : '1px solid rgba(0, 0, 0, 0.1)',
                  backgroundColor: enableDarkTheme
                    ? 'rgba(255, 255, 255, 0.02)'
                    : 'rgba(0, 0, 0, 0.02)',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.9)'
                      : 'rgba(0, 0, 0, 0.9)',
                    marginBottom: '4px',
                  }}
                >
                  {t('sensor_overview')}{' '}
                  <span
                    style={{
                      fontSize: '13px',
                      color: enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.6)'
                        : 'rgba(0, 0, 0, 0.6)',
                    }}
                  >
                    {t('sensors_considered_for_brakes_struts')}
                  </span>
                </Typography>
              </Box>
              <Table
                sx={{
                  '& .MuiTableHead-root': {
                    backgroundColor: enableDarkTheme
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.05)',
                  },
                  '& .MuiTableRow-root': {
                    '&:nth-of-type(even)': {
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.02)'
                        : 'rgba(0, 0, 0, 0.02)',
                    },
                    '&:hover': {
                      backgroundColor: enableDarkTheme
                        ? 'rgba(255, 255, 255, 0.08)'
                        : 'rgba(0, 0, 0, 0.08)',
                    },
                  },
                  '& .MuiTableCell-root': {
                    borderBottom: enableDarkTheme
                      ? '1px solid rgba(255, 255, 255, 0.1)'
                      : '1px solid rgba(0, 0, 0, 0.1)',
                    padding: '12px 16px',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.9)'
                          : 'rgba(0, 0, 0, 0.9)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '16px',
                      }}
                    >
                      {t('sensor_name')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.9)'
                          : 'rgba(0, 0, 0, 0.9)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '16px',
                      }}
                    >
                      {t('description')}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: enableDarkTheme
                          ? 'rgba(255, 255, 255, 0.9)'
                          : 'rgba(0, 0, 0, 0.9)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '16px',
                        textAlign: 'center',
                      }}
                    >
                      Threshold
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {brakeData &&
                    Array.from(
                      new Set(
                        brakeData.map(
                          (item: any) =>
                            item.device_sensor?.S?.split('-')[1]?.replace(
                              /_/g,
                              ' '
                            ) || 'Unknown'
                        )
                      )
                    ).map((sensorName, index) => {
                      // Get the latest data for this sensor
                      const sensorData = brakeData
                        .filter(
                          (item: any) =>
                            item.device_sensor?.S?.split('-')[1]?.replace(
                              /_/g,
                              ' '
                            ) === sensorName
                        )
                        .sort(
                          (a: any, b: any) =>
                            parseInt(b.ts.N) - parseInt(a.ts.N)
                        )[0];

                      const latestValue = sensorData
                        ? parseFloat(sensorData.reds_percentage.N)
                        : 0;
                      const threshold = sensorData
                        ? parseFloat(sensorData.reds_percentage.N)
                        : 0;

                      return (
                        <TableRow key={index}>
                          <TableCell
                            sx={{
                              padding: '16px',
                              verticalAlign: 'top',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Box
                                sx={{
                                  width: '8px',
                                  height: '8px',
                                  borderRadius: '50%',
                                  backgroundColor:
                                    threshold > 0.66
                                      ? '#F44336'
                                      : threshold > 0.33
                                      ? '#FF9800'
                                      : '#4CAF50',
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: enableDarkTheme
                                    ? 'rgba(255, 255, 255, 0.9)'
                                    : 'rgba(0, 0, 0, 0.9)',
                                }}
                              >
                                {sensorName as string}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '16px',
                              verticalAlign: 'top',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '12px',
                                color: enableDarkTheme
                                  ? 'rgba(255, 255, 255, 0.7)'
                                  : 'rgba(0, 0, 0, 0.7)',
                                lineHeight: 1.4,
                                maxWidth: '200px',
                                wordWrap: 'break-word',
                              }}
                            >
                              {sensorData?.description ||
                                'No description available'}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              padding: '16px',
                              textAlign: 'center',
                              verticalAlign: 'top',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 12px',
                                borderRadius: '16px',
                                backgroundColor:
                                  threshold > 0.66
                                    ? 'rgba(244, 67, 54, 0.15)'
                                    : threshold > 0.33
                                    ? 'rgba(255, 152, 0, 0.15)'
                                    : 'rgba(76, 175, 80, 0.15)',
                                border: `1px solid ${
                                  threshold > 0.66
                                    ? 'rgba(244, 67, 54, 0.3)'
                                    : threshold > 0.33
                                    ? 'rgba(255, 152, 0, 0.3)'
                                    : 'rgba(76, 175, 80, 0.3)'
                                }`,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 700,
                                  color:
                                    threshold > 0.66
                                      ? '#F44336'
                                      : threshold > 0.33
                                      ? '#FF9800'
                                      : '#4CAF50',
                                }}
                              >
                                {threshold.toFixed(1)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Chassis Analysis Section */}
        {selectedAnalysisSection === 'chassis' && <></>}

        {/* Engine Analysis Section */}
        {selectedAnalysisSection === 'engine' && <></>}
      </RightSection>
    </Box>
  );
};

export default MainBody;
